import { Check, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LoanService } from "services/LoanService";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import {
  AnnualTurnoverSchema,
  BrokerCommissionRateSchema,
  EstimatedExpenditureSchema,
  EstimatedFilingDateSchema,
  HasATOSchema,
  HasATOTaxIncentiveSchema,
  LoanAmountSchema,
  TaxYearSchema,
} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";
import dayjs from "dayjs";
import { CloseDialogIcon } from "assets/styles/icons";
import { BROKER_COMMISSION_RATE } from "shared/helpers/Constants";

export const handleSaveLoanRND = (
  currentProfile,
  loanId,
  callback,
  formik = null,
  isBroker,
) => {
  LoanService.saveRNDLoan(
    {
      annualTurnover: currentProfile.annualTurnover
        ? parseFloat(String(currentProfile.annualTurnover).replace(/,/g, ""))
        : null,
      hasATOClaims: currentProfile.hasATOClaims,
      hasTaxIncentive: currentProfile.hasTaxIncentive,
      taxYear: currentProfile.taxYear,
      estimatedTaxFilingDate: currentProfile.estimatedTaxFilingDate,
      estimatedExpenditureAmount: currentProfile.estimatedExpenditureAmount
        ? parseFloat(
            String(currentProfile.estimatedExpenditureAmount).replace(/,/g, ""),
          )
        : null,
      loanAmount: currentProfile.loanAmount
        ? parseFloat(String(currentProfile.loanAmount).replace(/,/g, ""))
        : null,
      brokerCommissionRate: !isBroker ? 0 : currentProfile.brokerCommissionRate,
    },
    loanId,
  )
    .then((response: any) => {
      callback(true);
    })
    .catch((error: any) => {
      callback(false);
      Utils.handleErrorResponse(error);
    })
    .finally(() => {
      formik?.setSubmitting(false);
    });
};

const LoanDetails = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const [isSubmitLoader, setSubmitLoader] = useState(false);
  const [dialogRndDisable, setDialogRnDDisable] = useState("");
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = isBroker ? 9 : 8;
  const PAGE_ID = "intro-loan";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/rnd/${PAGE_ID}/1`, { replace: true });
    }
  }, [currentStep, isValidStep, navigate]);

  const handleSubmitForm = (values: any, formik = null) => {
    dispatch(ProfileActions.setRndProfile(values));

    const nextStep = currentStep + 1;

    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    } else {
      if (currentProfile.isAllowedToEdit) {
        if (!currentProfile.isAllowedToEdit?.yourLoan) {
          handleCompleteAndNext();
          return;
        }
      }
      handleSaveLoanRND(
        {
          ...currentProfile,
          ...(!isBroker
            ? { estimatedExpenditureAmount: values.estimatedExpenditureAmount }
            : { brokerCommissionRate: values.brokerCommissionRate }),
        },
        currentProfile.loadId,
        (ev) => {
          if (ev) {
            handleCompleteAndNext();
          } else {
            setSubmitLoader(false);
          }
        },
        formik,
        isBroker,
      );
    }
  };

  if (!isValidStep) {
    return null;
  }

  return (
    <>
      {currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LoanAmountSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              loanAmount: Utils.formatCurrency(currentProfile.loanAmount),
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Loan details" : "Your loan"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "How much money your customer is looking to borrow?"
                    : "How much money are you looking to borrow?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter amount"
                  label=""
                  name="loanAmount"
                  outerLabel
                  className="ezifin-default-field"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  customIsoDollar
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={AnnualTurnoverSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              annualTurnover: Utils.formatCurrency(
                currentProfile.annualTurnover,
              ),
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Loan details" : "Your loan"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  What is the business’s annual turnover?
                </Typography>
                <AppFormikTextField
                  placeholder="Enter amount"
                  label=""
                  name="annualTurnover"
                  outerLabel
                  className="ezifin-default-field"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  customIsoDollar
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 3 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={HasATOSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              hasATOClaims: currentProfile.hasATOClaims,
            }}
          >
            {({ handleSubmit, errors, values, setFieldValue }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "Have your customer lodged prior or current R&D claims with the ATO?"
                      : "Have you lodged prior or current R&D claims with the ATO?"}
                  </Typography>
                  <Box mt={2}>
                    <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                      {["Yes", "No"].map((option) => (
                        <Chip
                          key={option}
                          label={option}
                          clickable
                          variant="outlined"
                          color={
                            values.hasATOClaims === (option === "Yes")
                              ? "primary"
                              : "default"
                          }
                          icon={
                            values.hasATOClaims === (option === "Yes") ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() => {
                            setFieldValue("hasATOClaims", option === "Yes");
                            if (option === "No") setDialogRnDDisable("lodged");
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={
                        Object.keys(errors).length > 0 || !values.hasATOClaims
                      }
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 4 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={HasATOTaxIncentiveSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              hasTaxIncentive: currentProfile.hasTaxIncentive,
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    Is this R&D eligible under the ATO R&D tax incentive?
                  </Typography>
                  <Box mt={2}>
                    <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                      {["Yes", "No"].map((option) => (
                        <Chip
                          key={option}
                          label={option}
                          clickable
                          variant="outlined"
                          color={
                            values.hasTaxIncentive === (option === "Yes")
                              ? "primary"
                              : "default"
                          }
                          icon={
                            values.hasTaxIncentive === (option === "Yes") ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() => {
                            setFieldValue("hasTaxIncentive", option === "Yes");
                            if (option === "No") setDialogRnDDisable("tax");
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      disabled={
                        Object.keys(errors).length > 0 ||
                        !values.hasTaxIncentive
                      }
                      onClick={handleSubmit}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 5 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={TaxYearSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              taxYear: currentProfile.taxYear,
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => {
              const taxYears = [2022, 2023, 2024, 2025, 2026];
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    Which tax year does this R&D apply?
                  </Typography>
                  {/* CHIPS */}
                  <Box mt={2} sx={{ display: "flex", gap: "0.5rem" }}>
                    {taxYears.map((option) => (
                      <Box mt={2} key={option}>
                        <Chip
                          label={option}
                          variant="outlined"
                          color={
                            values.taxYear === option ? "primary" : "default"
                          }
                          icon={
                            values.taxYear === option ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() => setFieldValue("taxYear", option)}
                          clickable
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 6 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={EstimatedFilingDateSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              estimatedTaxFilingDate: currentProfile.estimatedTaxFilingDate,
            }}
          >
            {({ handleSubmit, errors, values, setValues }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    Estimated tax filing date?
                  </Typography>
                  <Box
                    mt={3}
                    sx={{
                      border: "1px solid #e7e7e7",
                      borderRadius: "6px",
                    }}
                  >
                    <DateCalendar
                      disabled={
                        currentProfile.isAllowedToEdit?.yourLoan === null
                          ? false
                          : currentProfile.isAllowedToEdit?.yourLoan ===
                              undefined
                            ? false
                            : !currentProfile.isAllowedToEdit?.yourLoan
                      }
                      minDate={dayjs()}
                      maxDate={dayjs().add(391, "day")}
                      onChange={(date) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");
                        setValues({
                          estimatedTaxFilingDate: formattedDate,
                        });
                      }}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#2d68f7",
                              color: "white",
                            },
                          },
                        },
                      }}
                      sx={{
                        "&.MuiDateCalendar-root": {
                          maxWidth: "100%",
                        },
                      }}
                      {...(currentProfile.estimatedTaxFilingDate
                        ? { value: dayjs(values.estimatedTaxFilingDate) }
                        : {})}
                    />
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 7 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={EstimatedExpenditureSchema}
            validateOnMount
            onSubmit={(formValues, formik) => {
              if (isBroker) {
                handleSubmitForm(formValues, formik);
              } else {
                setSubmitLoader(true);
                handleSubmitForm(formValues, formik);
              }
            }}
            initialValues={{
              estimatedExpenditureAmount: Utils.formatCurrency(
                currentProfile.estimatedExpenditureAmount,
              ),
            }}
          >
            {({ handleSubmit, errors, values }) => {
              console.log(values);
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    R&D estimated expenditure amount-
                  </Typography>

                  <AppFormikTextField
                    placeholder="Enter amount"
                    label=""
                    name="estimatedExpenditureAmount"
                    outerLabel
                    className="ezifin-default-field"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    customIsoDollar
                  />

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={isSubmitLoader}
                      loadingText="Please wait.."
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit();
                      }}
                      disabled={
                        Object.keys(errors).length > 0 || isSubmitLoader
                      }
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {isBroker && currentStep === 8 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={BrokerCommissionRateSchema}
            validateOnMount
            onSubmit={(formValues, formik) => {
              setSubmitLoader(true);
              handleSubmitForm(formValues, formik);
            }}
            initialValues={{
              brokerCommissionRate: currentProfile.brokerCommissionRate,
            }}
          >
            {({ handleSubmit, errors, isSubmitting, values }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">Loan details</Typography>
                  <Typography variant="h3" mt={3}>
                    Broker commission rate (ex. GST):
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="brokerCommissionRate"
                        items={BROKER_COMMISSION_RATE}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={isSubmitLoader}
                      loadingText="Please wait.."
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit();
                      }}
                      disabled={
                        values.brokerCommissionRate === 0 ||
                        Object.keys(errors).length > 0 ||
                        isSubmitLoader
                      }
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      <Dialog open={dialogRndDisable !== ""} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">
              {dialogRndDisable === "lodged" && "R&D Claim History"}
              {dialogRndDisable === "tax" && "R&D Eligibility Check"}
            </Typography>
            <IconButton
              onClick={() => {
                setDialogRnDDisable("");
              }}
            >
              <CloseDialogIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <>
          <DialogContent>
            <Typography variant="h3" fontWeight={400} mb={3}>
              {dialogRndDisable === "lodged" && isBroker
                ? "To help assess your customer eligibility, please let us know whether your customer have lodged any prior or current R&D claims with the ATO."
                : dialogRndDisable === "lodged" && !isBroker
                  ? "To help assess your eligibility, please let us know whether you’ve lodged any prior or current R&D claims with the ATO."
                  : ""}
              {dialogRndDisable === "tax" && isBroker
                ? "To help assess your customer eligibility, please confirm whether this activity is considered eligible under the ATO’s R&D Tax Incentive."
                : dialogRndDisable === "tax" && !isBroker
                  ? " To help assess your eligibility, please confirm whether this activity is considered eligible under the ATO’s R&D Tax Incentive."
                  : ""}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ gap: "12px" }}>
            <AppPrimaryButton
              title="Cancel"
              className="btn-small"
              onClick={() => {
                setDialogRnDDisable("");
              }}
              fullWidth
            />
          </DialogActions>
        </>
      </Dialog>
    </>
  );
};

export default LoanDetails;
